import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, makeStyles } from '@material-ui/core';

import { Organization } from '../../types/organization.type';
import { useAdminGetQuotes } from '../../../quotes/hooks/get-quotes.hook';
import { useReadyQuote } from '../../../quotes/hooks/ready-quote.hook';
import { useSendQuote } from '../../../quotes/hooks/send-quote.hook';
import { useAcceptQuote } from '../../../quotes/hooks/accept-quote.hook';
import { useRefuseQuote } from '../../../quotes/hooks/refuse-quote.hook';
import { useCancelQuote } from '../../../quotes/hooks/cancel-quote.hook';
import { useDuplicateQuote } from '../../../quotes/hooks/duplicate-quote.hook';
import { useGetQuoteDownloadUrl } from '../../../quotes/hooks/get-quote-download-url.hook';
import { Quote } from '../../../quotes/types/quote.type';
import { QuoteStatus, QuoteStatuses } from '../../../quotes/types/quote-status.type';
import { QuoteAction, QuotesTable } from '../../../quotes/components/Tables/QuotesTable.component';
import { CreateQuoteDialog } from '../../../quotes/components/Modals/CreateQuoteDialog.component';
import { EditQuoteDialog } from '../../../quotes/components/Modals/EditQuoteDialog.component';
import { ConfirmQuoteActionDialog } from '../../../quotes/components/Modals/ConfirmQuoteActionDialog.component';
import { ADMIN_ORDER_APP_PATHS } from '../../../orders/constants/orders-app-paths.constant';

type Props = {
  organization: Organization;
  refreshOrganization: () => Promise<void> | void;
};

type ConfirmableAction = 'accept' | 'refuse' | 'cancel';

type DialogState =
  | { kind: 'none' }
  | { kind: 'create' }
  | { kind: 'edit'; quote: Quote }
  | { kind: 'confirm'; quote: Quote; action: ConfirmableAction };

const DESTRUCTIVE: ReadonlySet<QuoteStatus> = new Set([
  QuoteStatuses.ACCEPTED,
  QuoteStatuses.REFUSED,
  QuoteStatuses.CANCELLED,
]);

const TARGET_TO_ACTION: Partial<Record<QuoteStatus, ConfirmableAction>> = {
  [QuoteStatuses.ACCEPTED]: 'accept',
  [QuoteStatuses.REFUSED]: 'refuse',
  [QuoteStatuses.CANCELLED]: 'cancel',
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  ctaButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

export function OrganizationQuotesTab({ organization, refreshOrganization }: Props) {
  const classes = useStyles();
  const history = useHistory();

  const { isLoading, quotes, refetch } = useAdminGetQuotes({ organizationId: organization.id });
  const { readyQuote } = useReadyQuote();
  const { sendQuote } = useSendQuote();
  const { acceptQuote, isSubmitting: isAccepting } = useAcceptQuote();
  const { refuseQuote, isSubmitting: isRefusing } = useRefuseQuote();
  const { cancelQuote, isSubmitting: isCanceling } = useCancelQuote();
  const { duplicateQuote } = useDuplicateQuote();
  const { getDownloadUrl } = useGetQuoteDownloadUrl();

  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });

  const closeDialog = () => setDialog({ kind: 'none' });

  const handleAction = async (action: QuoteAction, quote: Quote) => {
    switch (action) {
      case 'edit':
        setDialog({ kind: 'edit', quote });
        return;
      case 'duplicate': {
        const duplicated = await duplicateQuote(quote.id);
        if (duplicated) await refetch();
        return;
      }
      case 'download': {
        const url = await getDownloadUrl(quote.id);
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
      case 'view-order':
        if (quote.orderId) {
          history.push(ADMIN_ORDER_APP_PATHS.ORDER_DETAILS(quote.orderId));
        } else {
          // eslint-disable-next-line no-console
          console.warn('Quote has no orderId; cannot navigate to order');
        }
        return;
      default: {
        const _exhaustive: never = action;
        // eslint-disable-next-line no-console
        console.warn(`Unhandled quote action: ${_exhaustive as string}`);
      }
    }
  };

  const handleStatusChange = async (quote: Quote, next: QuoteStatus): Promise<void> => {
    if (DESTRUCTIVE.has(next)) {
      const action = TARGET_TO_ACTION[next];
      if (!action) return;
      setDialog({ kind: 'confirm', quote, action });
      return;
    }
    if (next === QuoteStatuses.READY) {
      const result = await readyQuote(quote.id);
      if (result) await refetch();
      return;
    }
    if (next === QuoteStatuses.SENT) {
      const result = await sendQuote(quote.id);
      if (result) await refetch();
      return;
    }
  };

  const handleConfirm = async () => {
    if (dialog.kind !== 'confirm') return;
    const { action, quote } = dialog;

    let success: Quote | null = null;
    switch (action) {
      case 'accept':
        success = await acceptQuote(quote.id);
        break;
      case 'refuse':
        success = await refuseQuote(quote.id);
        break;
      case 'cancel':
        success = await cancelQuote(quote.id);
        break;
      default: {
        const _exhaustive: never = action;
        throw new Error(`Unsupported confirm action: ${_exhaustive as string}`);
      }
    }

    if (success) {
      await refetch();
      if (action === 'accept') {
        await refreshOrganization();
      }
      closeDialog();
    }
  };

  const confirmIsSubmitting =
    dialog.kind === 'confirm' &&
    ((dialog.action === 'accept' && isAccepting) ||
      (dialog.action === 'refuse' && isRefusing) ||
      (dialog.action === 'cancel' && isCanceling));

  return (
    <section className={classes.root}>
      <div className={classes.header}>
        <Button
          size="small"
          variant="contained"
          className={classes.ctaButton}
          onClick={() => setDialog({ kind: 'create' })}
        >
          Créer
        </Button>
      </div>

      <QuotesTable quotes={quotes} isLoading={isLoading} onAction={handleAction} onStatusChange={handleStatusChange} />

      {dialog.kind === 'create' && (
        <CreateQuoteDialog
          open
          organization={organization}
          organizationId={organization.id}
          onCreated={() => {
            void refetch();
          }}
          onClose={closeDialog}
        />
      )}

      {dialog.kind === 'edit' && (
        <EditQuoteDialog
          open
          organization={organization}
          quote={dialog.quote}
          onUpdated={() => {
            void refetch();
          }}
          onClose={closeDialog}
        />
      )}

      {dialog.kind === 'confirm' && (
        <ConfirmQuoteActionDialog
          open
          action={dialog.action}
          quote={dialog.quote}
          isSubmitting={confirmIsSubmitting}
          onConfirm={handleConfirm}
          onClose={closeDialog}
        />
      )}
    </section>
  );
}
